import { Link } from "react-router-dom";
import { BadgeCheck, CalendarClock, Clock, IndianRupee, ShieldCheck, Stethoscope } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { CallButton, WhatsAppButton } from "@/components/common/ContactButtons";
import { SpineMotif } from "@/components/brand/SpineMotif";
import { ImageSlot } from "@/components/common/ImageSlot";
import { hoursSummary } from "@/lib/contact";
import { inr } from "@/lib/format";
import { PAGES } from "@/lib/seo";
import { InteractiveSpine } from "./InteractiveSpine";

const Chip = ({ icon: Icon, tone, testid, children }) => (
  <li className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold ring-1 ${tone}`} data-testid={testid}>
    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
    {children}
  </li>
);

const DoctorStrip = () => {
  const { settings, content } = useSite();
  const facts = [
    [BadgeCheck, content.doctor_credentials, "text-brand-blue"],
    [Stethoscope, content.doctor_experience, "text-brand-greenDark"],
    [BadgeCheck, content.doctor_registration, "text-brand-coralDeep"],
  ].filter(([, text]) => text);
  return (
    <div
      className="grid gap-5 rounded-[2rem] bg-white/90 p-5 shadow-[0_30px_60px_-38px_rgba(1,78,150,0.45)] ring-1 ring-slate-200/70 backdrop-blur sm:p-6 lg:grid-cols-[auto_1fr_auto] lg:items-center lg:gap-8"
      data-testid="hero-doctor-card"
    >
      <div className="flex items-center gap-4">
        <ImageSlot image={content.doctor_portrait} label="Doctor portrait" ratio="aspect-square" className="h-16 w-16 shrink-0 rounded-full ring-4 ring-brand-skyTint [&_span]:hidden sm:h-20 sm:w-20" testid="hero-portrait-slot" />
        <div>
          <p className="font-heading text-lg font-bold text-brand-ink sm:text-xl">{content.doctor_name}</p>
          <p className="text-sm text-slate-600">{content.doctor_designation}</p>
        </div>
      </div>
      <ul className="grid gap-2 sm:grid-cols-3 sm:gap-4">
        {facts.map(([Icon, text, tone]) => (
          <li key={text} className="flex items-start gap-2 text-sm text-slate-700">
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} aria-hidden="true" /> {text}
          </li>
        ))}
      </ul>
      <p className="flex items-center gap-2 rounded-2xl bg-brand-tint px-4 py-3 text-sm font-medium text-brand-ink">
        <CalendarClock className="h-4 w-4 shrink-0 text-brand-blue" aria-hidden="true" /> {settings.hours_note}
      </p>
    </div>
  );
};

export const Hero = () => {
  const { settings } = useSite();
  const home = PAGES["/"];
  return (
    <section className="mesh-hero grain relative overflow-hidden" data-testid="home-hero">
      <span className="blob -right-24 top-6 h-72 w-72 animate-float bg-brand-sky/25" aria-hidden="true" />
      <span className="blob -left-24 bottom-24 h-80 w-80 bg-brand-lime/25" aria-hidden="true" />
      <span className="blob right-1/3 top-1/2 h-56 w-56 bg-brand-sun/20" aria-hidden="true" />
      <SpineMotif className="pointer-events-none absolute -left-6 top-8 hidden h-[560px] text-brand-blue/[0.06] lg:block" count={16} />
      <div className="container-x relative grid items-center gap-10 py-12 md:py-16 lg:grid-cols-12 lg:gap-14 lg:py-20">
        <div className="lg:col-span-7">
          <p className="eyebrow animate-rise">{home.eyebrow}</p>
          <h1 className="animate-rise delay-1 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl">
            Precision Spine Care for a <span className="text-gradient">Pain-Free</span> Life
          </h1>
          <p className="animate-rise delay-2 mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">{home.intro}</p>
          <ul className="animate-rise delay-2 mt-6 flex flex-wrap gap-2" aria-label="Clinic at a glance">
            <Chip icon={Clock} tone="bg-brand-skyTint text-brand-skyDeep ring-brand-sky/25" testid="hero-chip-hours">{hoursSummary(settings.schedule)}</Chip>
            <Chip icon={IndianRupee} tone="bg-brand-greenTint text-brand-greenDeep ring-brand-green/25" testid="hero-chip-fee">{inr(settings.consultation_fee)} consultation</Chip>
            <Chip icon={ShieldCheck} tone="bg-brand-warmTint text-brand-coralDeep ring-brand-coral/25" testid="hero-chip-approach">No injections · No surgery</Chip>
          </ul>
          <div className="animate-rise delay-3 mt-8 flex flex-col gap-3 sm:flex-row">
            <CallButton size="lg" testid="hero-call-btn" />
            <WhatsAppButton size="lg" testid="hero-whatsapp-btn" />
          </div>
          <Link to="/shop/spine-fit-tablets" data-testid="hero-shop-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-greenDark underline-offset-4 hover:underline">
            Shop Spine-Fit tablets →
          </Link>
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
            Spine Health <span className="text-brand-green">|</span> Pain Relief <span className="text-brand-coral">|</span> Better Tomorrow
          </p>
        </div>
        <div className="animate-rise delay-2 lg:col-span-5">
          <InteractiveSpine />
        </div>
      </div>
      <div className="container-x relative pb-12 md:pb-16">
        <DoctorStrip />
      </div>
    </section>
  );
};
